import React, { useContext } from 'react'
import {
  makeStyles,
  ButtonIcon,
  DescriptionIcon,
  DividerIcon,
  ImageSmallIcon,
  QuoteIcon,
  SpacerIcon,
  TwoColIcon,
  ThreeColIcon
} from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import { ButtonPopupMenu } from '../components'
import { AddButton } from '../components/ButtonPopupMenu'
import CropPortraitIcon from '@material-ui/icons/CropPortrait'
import AccountCircleOutlinedIcon from '@material-ui/icons/AccountCircleOutlined'
import AssignmentOutlinedIcon from '@material-ui/icons/AssignmentOutlined'
import FormatAlignCenterIcon from '@material-ui/icons/FormatAlignCenter'
import InsertDriveFileOutlinedIcon from '@material-ui/icons/InsertDriveFileOutlined'
import PhotoLibraryOutlinedIcon from '@material-ui/icons/PhotoLibraryOutlined'
import PhotoAlbumOutlinedIcon from '@material-ui/icons/PhotoAlbumOutlined'
import FormatQuoteIcon from '@material-ui/icons/FormatQuoteOutlined'
import MenuIcon from '@material-ui/icons/Menu'
import ShareIcon from '@material-ui/icons/Share'
import CodeIcon from '@material-ui/icons/Code'
import CompareIcon from '@material-ui/icons/Compare'
import { PresentationSectionSelectDialog } from '../dialogs'
import type { PresentationOptionV2, PresentationSectionV2 } from 'paintscout'
import type { LabelsOption } from '@paintscout/util/builder'
import isEqual from 'lodash/isEqual'
import { DialogStackContext, useUser } from '@paintscout/ui-business'

interface AddBlockButtonProps {
  addPages: boolean
  classes?: StyleClasses<typeof useStyles>
  focused?: boolean
  // little sketch using id here instead of ref
  // TODO over to ref if need more than one of these with an id on a page
  id?: string
  objectLabels: LabelsOption
  onActionClick: any
  onAddSection: (newSection: PresentationSectionV2) => void
  presentation: PresentationOptionV2
}

const useStyles = makeStyles<Theme, AddBlockButtonProps>(
  (theme: Theme) => {
    return {
      root: (props) => ({
        textAlign: 'center',
        position: 'relative',
        height: 0,
        display: 'flex',
        zIndex: props.focused ? 3 : 2,
        alignItems: 'center',
        justifyContent: 'center'
      }),
      menu: {
        boxShadow: theme.boxShadow[1]
      },
      button: (props) => ({
        color: theme.palette.presentation.sectionBorder.hover,
        borderStyle: 'solid',
        borderColor: theme.palette.presentation.sectionBorder.hover,
        borderWidth: '2px',
        backgroundColor: 'white',
        '&:hover': {
          backgroundColor: '#f5f5f5'
        },
        ...(props.focused && {
          color: theme.palette.presentation.sectionBorder.focus,
          borderColor: theme.palette.presentation.sectionBorder.focus
        })
      }),
      rule: {}
    }
  },
  { name: 'AddBlockButton' }
)

function AddBlockButton(props: AddBlockButtonProps) {
  const classes = useStyles(props)
  const { addPages, id, objectLabels, onActionClick, onAddSection, presentation } = props
  const { isSuperadmin } = useUser()
  const actions = [
    {
      key: 'logo',
      label: 'Logo',
      icon: AccountCircleOutlinedIcon
    },
    {
      key: 'banner',
      label: 'Banner',
      icon: PhotoAlbumOutlinedIcon
    },
    {
      key: 'text',
      label: 'Text',
      icon: DescriptionIcon
    },
    {
      key: 'image',
      label: 'Media',
      icon: ImageSmallIcon
    },
    {
      key: '2-column',
      label: '2 Column',
      icon: TwoColIcon
    },
    {
      key: '3-column',
      label: '3 Column',
      icon: ThreeColIcon
    },
    {
      key: 'cards',
      label: 'Cards',
      icon: CropPortraitIcon
    },
    {
      key: 'gallery',
      label: 'Gallery',
      icon: PhotoLibraryOutlinedIcon
    },
    {
      key: 'testimonials',
      label: 'Testimonials',
      icon: FormatQuoteIcon
    },
    {
      key: 'button',
      label: 'Button',
      icon: ButtonIcon
    },
    {
      key: 'social-links',
      label: 'Social Links',
      icon: ShareIcon
    },
    {
      key: 'divider',
      label: 'Divider',
      icon: DividerIcon
    },
    {
      key: 'spacer',
      label: 'Spacer',
      icon: SpacerIcon
    },
    {
      key: 'quote',
      label: objectLabels.quote.value,
      icon: QuoteIcon
    },
    {
      key: 'terms',
      label: 'Terms',
      icon: MenuIcon
    },
    {
      key: 'custom',
      label: 'Custom HTML/CSS',
      icon: CodeIcon
    },
    {
      key: 'slider-image',
      label: 'Slider Image',
      icon: CompareIcon
    },
    // pictures intentionally omitted from the add-menu (#21821): it stays supported as a
    // render target + converter output for v1 sections carried over, but isn't offered
    // as a fresh block to add in v2 while product decides on the catalog
    // v1-parity sections, superadmin-only while product decides on the catalog (#21312)
    ...(isSuperadmin
      ? [
          {
            key: 'about-estimator',
            label: 'About Your Estimator',
            icon: AccountCircleOutlinedIcon
          },
          {
            key: 'awards',
            label: 'Images Row',
            icon: PhotoLibraryOutlinedIcon
          },
          {
            key: 'documents',
            label: 'Documents',
            icon: InsertDriveFileOutlinedIcon
          },
          {
            key: 'mission',
            label: 'Centered Text',
            icon: FormatAlignCenterIcon
          },
          {
            key: 'services',
            label: 'Two Column Rows',
            icon: TwoColIcon
          },
          {
            key: 'work-order',
            label: objectLabels.workOrder.value,
            icon: AssignmentOutlinedIcon
          }
        ]
      : [])
  ]

  const { openDialog, dismissDialog } = useContext(DialogStackContext)

  const handleAddSection = (ev) => {
    ev.stopPropagation()
    openDialog(PresentationSectionSelectDialog, {
      onConfirm: (newSection: PresentationSectionV2) => {
        onAddSection(newSection)
        dismissDialog()
      },
      presentation,
      onCancel: () => {
        dismissDialog()
      }
    })
  }

  if (addPages) {
    return (
      <div className={classes.root}>
        <div className={classes.rule}></div>
        <AddButton
          buttonSize={'small'}
          id={id}
          onClick={handleAddSection}
          className={classes.button}
          buttonVariant={'outlined'}
        />
      </div>
    )
  }

  return (
    <div className={classes.root}>
      <div className={classes.rule}></div>
      <ButtonPopupMenu
        actions={actions}
        buttonSize={'small'}
        buttonVariant={'outlined'}
        columnCount={3}
        id={id}
        onActionClick={onActionClick}
        tooltipTitle={`Add Block`}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
        {...({ classes: { button: classes.button, menu: classes.menu } } as any)}
      />
    </div>
  )
}

export default React.memo(AddBlockButton, (prevProps, nextProps) => {
  // keep this up to date with any new props
  if (prevProps.focused !== nextProps.focused) return false
  if (prevProps.presentation.advanced.industry !== nextProps.presentation.advanced.industry) return false
  if (!isEqual(prevProps.presentation.advanced.style, nextProps.presentation.advanced.style)) return false
  if (!isEqual(prevProps.classes, nextProps.classes)) return false
  if (!isEqual(prevProps.objectLabels, nextProps.objectLabels)) return false

  return true
})
